import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchPage, fetchGalleries } from '../lib/cms';
import GalleryModal from '../components/GalleryModal';
import { useScrollReveal } from '../hooks/useScrollReveal';

export default function Gallery() {
  const [pageData, setPageData] = useState(null);
  const [galleries, setGalleries] = useState([]);
  const [activeTab, setActiveTab] = useState('alle');
  const [loading, setLoading] = useState(true);

  // Lightbox state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);
  const [lightboxImages, setLightboxImages] = useState([]);

  useEffect(() => {
    async function loadGalleryData() {
      const [page, allGalleries] = await Promise.all([
        fetchPage('page-galleri'),
        fetchGalleries()
      ]);
      setPageData(page);
      setGalleries(allGalleries);
      setLoading(false);
    }
    loadGalleryData();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-sand-50">
        <div className="text-center">
          <i className="fa-solid fa-spinner animate-spin text-4xl text-safety-orange"></i>
          <p className="mt-4 text-sm font-semibold text-asphalt-600">Henter gallerier...</p>
        </div>
      </div>
    );
  }

  const metaTitle = pageData?.meta_title || 'Galleri - JE Entreprenør';
  const metaDesc = pageData?.meta_description || 'Se billeder af vores udførte arbejde inden for asfalt, rørlægning, entreprenørarbejde og mandskabsudlejning på Sydfyn.';
  const heroTitle = pageData?.hero_title || 'Vores Projekter';
  const heroSubtitle = pageData?.hero_subtitle || 'Se et udvalg af de opgaver, vi har løst for tilfredse kunder på hele Sydfyn.';

  // Filter images based on active tab
  const getFilteredImages = () => {
    if (activeTab === 'alle') {
      // Flatten all images with their category
      return galleries.flatMap(g => 
        (g.images || []).map(img => ({ url: img, category: g.title, slug: g.slug.da || g.slug }))
      );
    }
    const selectedGallery = galleries.find(g => (g.slug.da || g.slug) === activeTab);
    return (selectedGallery?.images || []).map(img => ({
      url: img,
      category: selectedGallery.title,
      slug: selectedGallery.slug.da || selectedGallery.slug
    }));
  };

  const filteredItems = getFilteredImages();

  const handleOpenLightbox = (index) => {
    setLightboxImages(filteredItems.map(item => item.url));
    setCurrentImgIndex(index);
    setIsModalOpen(true);
  };

  const handlePrevImage = () => {
    setCurrentImgIndex((prev) => (prev === 0 ? lightboxImages.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImgIndex((prev) => (prev === lightboxImages.length - 1 ? 0 : prev + 1));
  };

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/ca189e60-8ca7-4520-a859-93fb9daa9500/hero-entreprenoer.jpg" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-asphalt-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-30">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/ca189e60-8ca7-4520-a859-93fb9daa9500/hero-entreprenoer.jpg"
            alt="JE Entreprenør galleri hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-asphalt-950/80" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-display text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">
            {heroTitle}
          </h1>
          <p className="mt-4 mx-auto max-w-2xl text-base text-asphalt-200">
            {heroSubtitle}
          </p>
        </div>
      </section>

      {/* Gallery Filter & Grid */}
      <section 
        className="py-16 bg-white"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div 
              className="text-base text-asphalt-600 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: pageData?.content || '' }}
            />
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-12 border-b border-asphalt-100 pb-6">
            <button
              onClick={() => setActiveTab('alle')}
              className={`rounded-lg px-5 py-2.5 text-sm font-bold transition-all duration-200 cursor-pointer ${
                activeTab === 'alle'
                  ? 'bg-safety-orange text-white shadow-md'
                  : 'bg-asphalt-50 text-asphalt-700 hover:bg-asphalt-100'
              }`}
            >
              Alle Projekter
            </button>
            {galleries.map((g) => {
              const slugStr = g.slug.da || g.slug;
              return (
                <button
                  key={slugStr}
                  onClick={() => setActiveTab(slugStr)}
                  className={`rounded-lg px-5 py-2.5 text-sm font-bold transition-all duration-200 cursor-pointer ${
                    activeTab === slugStr
                      ? 'bg-safety-orange text-white shadow-md'
                      : 'bg-asphalt-50 text-asphalt-700 hover:bg-asphalt-100'
                  }`}
                >
                  {g.title}
                </button>
              );
            })}
          </div>

          {/* Image Grid */}
          {filteredItems.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-asphalt-500">Der blev ikke fundet nogle billeder i dette galleri.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filteredItems.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleOpenLightbox(index)}
                  className="group relative h-72 w-full overflow-hidden rounded-xl border border-asphalt-200 bg-white shadow-sm hover:border-safety-orange transition-all duration-300 cursor-pointer"
                  aria-label={`Vis ${item.category} billede ${index + 1}`}
                >
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${item.url.replace('https://assets.auxodev.dk/', '')}`}
                    alt={`${item.category} projektbillede`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      <GalleryModal
        images={lightboxImages}
        currentIndex={currentImgIndex}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onPrev={handlePrevImage}
        onNext={handleNextImage}
      />
    </>
  );
}
